import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Fade,
  useTheme,
  alpha,
  Switch,
  FormControlLabel,
  Paper
} from '@mui/material';

import { useAuth } from '../contexts/AuthContext';
import { getAllCustomers } from '../services/customerService';
import { getAllMaintenanceRecords } from '../services/maintenanceService';
import { getAllInventoryItems } from '../services/inventoryService';
import { getTodaySalesTotal } from '../services/posService';
import { MaintenanceRecord, MaintenanceStatus } from '../utils/types';
import ModernDashboard from '../components/dashboard/ModernDashboard';
import RecentActivity from '../components/dashboard/RecentActivity';
import QuickDebtSummary from '../components/dashboard/QuickDebtSummary';

// Status colors for maintenance
const statusColors: Record<MaintenanceStatus, string> = {
  [MaintenanceStatus.PENDING]: 'warning',
  [MaintenanceStatus.IN_PROGRESS]: 'info',
  [MaintenanceStatus.COMPLETED]: 'success',
  [MaintenanceStatus.DELIVERED]: 'success',
  [MaintenanceStatus.CANCELLED]: 'error',
  [MaintenanceStatus.REFUNDED]: 'error',
  [MaintenanceStatus.RETURNED]: 'warning'
};

// Status labels in Arabic
const statusLabels: Record<MaintenanceStatus, string> = {
  [MaintenanceStatus.PENDING]: 'قيد الانتظار',
  [MaintenanceStatus.IN_PROGRESS]: 'قيد الصيانة',
  [MaintenanceStatus.COMPLETED]: 'تم الإصلاح',
  [MaintenanceStatus.DELIVERED]: 'تم التسليم',
  [MaintenanceStatus.CANCELLED]: 'ملغي',
  [MaintenanceStatus.REFUNDED]: 'مرتجع مع استرداد',
  [MaintenanceStatus.RETURNED]: 'مرتجع مع رفض الجهاز'
};

const DashboardPage: React.FC = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useModernView, setUseModernView] = useState(true);
  const [recentMaintenance, setRecentMaintenance] = useState<MaintenanceRecord[]>([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get maintenance records for recent activity
        const maintenanceRecords = await getAllMaintenanceRecords(undefined, 10);
        setRecentMaintenance(maintenanceRecords);

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('حدث خطأ أثناء تحميل البيانات. يرجى تحديث الصفحة.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Fade in timeout={500}>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={60} thickness={4} />
            <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
              جاري تحميل لوحة التحكم...
            </Typography>
          </Box>
        </Fade>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            borderRadius: 3
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            يرجى تحديث الصفحة أو المحاولة مرة أخرى
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Dashboard View Toggle */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <FormControlLabel
          control={
            <Switch
              checked={useModernView}
              onChange={(e) => setUseModernView(e.target.checked)}
              color="primary"
            />
          }
          label="العرض الحديث"
        />
      </Box>

      {useModernView ? (
        <>
          {/* Modern Dashboard */}
          <ModernDashboard />

          {/* Recent Activity */}
          <Grid container spacing={3} sx={{ mt: 4 }}>
            <Grid item xs={12}>
              <RecentActivity
                recentMaintenance={recentMaintenance}
                loading={loading}
              />
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          {/* Classic Dashboard View */}
          <Fade in timeout={500}>
            <Box>
              <Typography variant="h4" gutterBottom>
                مرحباً، {currentUser?.name}
              </Typography>

              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                إليك نظرة عامة على الإحصائيات الرئيسية
              </Typography>

              {/* Quick Debt Summary */}
              <Grid container spacing={3} sx={{ mt: 2, mb: 4 }}>
                <Grid item xs={12} md={6} lg={4}>
                  <QuickDebtSummary />
                </Grid>
              </Grid>

              {/* Recent Maintenance - Classic Style */}
              <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
                آخر عمليات الصيانة
              </Typography>

              <RecentActivity
                recentMaintenance={recentMaintenance}
                loading={loading}
              />
            </Box>
          </Fade>
        </>
      )}
    </Box>
  );
};

export default DashboardPage;
